import { ApiClientError, ApiClientTransportError } from '@contract-analyzer/api-client';

import type { SelectedUpload } from '@/lib/upload-selection';

export const SUPPORTED_EXTENSIONS = ['.txt', '.md', '.markdown', '.pdf', '.docx', '.zip'];

// Mirrors the server admission limits in apps/api domain/documents/intake.py and
// domain/upload_batches/models.py. The server stays authoritative; these limits let
// the browser refuse a selection before reserving capacity for it.
/** One server upload batch declares at most this many inputs. */
export const MAX_UPLOAD_BATCH_INPUTS = 1000;
/**
 * One selection takes at most this many inputs. The upload store declares a
 * larger selection than one batch holds as consecutive batches and shows them
 * as one upload; the cap bounds what a tab holds in memory and renders.
 */
export const MAX_UPLOAD_SELECTION_COUNT = 5000;
const MAX_ARCHIVE_ENTRY_COUNT = 1000;
const MAX_TEXT_UPLOAD_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const MAX_PDF_UPLOAD_FILE_SIZE_BYTES = 100 * 1024 * 1024;
const MAX_ARCHIVE_UPLOAD_FILE_SIZE_BYTES = 1024 * 1024 * 1024;
const MAX_TEXT_UPLOAD_FILE_SIZE_LABEL = '25 MB';
const MAX_PDF_UPLOAD_FILE_SIZE_LABEL = '100 MB';
const MAX_ARCHIVE_UPLOAD_FILE_SIZE_LABEL = '1 GB';
const MAX_ARCHIVE_EXPANDED_SIZE_LABEL = '5 GB';

/** One sentence every upload surface shows beside its file chooser. */
export const UPLOAD_LIMITS_SUMMARY = `PDF, DOCX, TXT, Markdown and ZIP · PDFs up to ${MAX_PDF_UPLOAD_FILE_SIZE_LABEL}; DOCX, TXT and Markdown up to ${MAX_TEXT_UPLOAD_FILE_SIZE_LABEL} · ZIPs up to ${MAX_ARCHIVE_UPLOAD_FILE_SIZE_LABEL}, ${MAX_ARCHIVE_EXPANDED_SIZE_LABEL} expanded and ${MAX_ARCHIVE_ENTRY_COUNT.toLocaleString('en')} files`;

const SUPPORTED_MIME_TYPES = [
  'text/plain',
  'text/markdown',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/zip',
];

export interface FileRejection {
  readonly name: string;
  /** Selected path; unique within one review. */
  readonly path: string;
  readonly reason: 'duplicate' | 'empty' | 'too_large' | 'unsupported';
  readonly limitLabel?: string;
}

/** The accepted part of an incoming selection and why the rest was refused. */
export interface SelectionReview {
  readonly accepted: readonly SelectedUpload[];
  readonly rejected: readonly FileRejection[];
}

/** A selection attempt refused whole because it would push the upload past the file limit. */
export interface SelectionLimitExceeded {
  /** Files that would have been added, had the limit allowed it. */
  readonly attemptedCount: number;
  /** Files already selected before this attempt. They are always kept. */
  readonly selectedCount: number;
  readonly limit: number;
}

function isSupportedFile(file: File): boolean {
  const normalizedName = file.name.toLowerCase();
  return (
    SUPPORTED_EXTENSIONS.some((extension) => normalizedName.endsWith(extension)) ||
    SUPPORTED_MIME_TYPES.includes(file.type)
  );
}

export function isArchiveFile(file: Pick<File, 'name' | 'type'>): boolean {
  const normalizedName = file.name.toLowerCase();
  return normalizedName.endsWith('.zip') || file.type === 'application/zip';
}

function sizeLimit(file: File) {
  if (isArchiveFile(file)) return { bytes: MAX_ARCHIVE_UPLOAD_FILE_SIZE_BYTES, label: MAX_ARCHIVE_UPLOAD_FILE_SIZE_LABEL };
  return file.name.toLowerCase().endsWith('.pdf')
    ? { bytes: MAX_PDF_UPLOAD_FILE_SIZE_BYTES, label: MAX_PDF_UPLOAD_FILE_SIZE_LABEL }
    : { bytes: MAX_TEXT_UPLOAD_FILE_SIZE_BYTES, label: MAX_TEXT_UPLOAD_FILE_SIZE_LABEL };
}

/** Selecting the same file under the same path twice adds it once. */
export function selectionKey({ file, relativePath }: SelectedUpload): string {
  return `${relativePath}\u0000${String(file.size)}\u0000${String(file.lastModified)}`;
}

function rejectionFor({ file, relativePath }: SelectedUpload): FileRejection | null {
  const refused = { name: file.name, path: relativePath };
  if (!isSupportedFile(file)) return { ...refused, reason: 'unsupported' };
  if (file.size === 0) return { ...refused, reason: 'empty' };
  const limit = sizeLimit(file);
  return file.size > limit.bytes ? { ...refused, reason: 'too_large', limitLabel: limit.label } : null;
}

/** Validate an incoming selection against the admission limits and what is already selected. */
export function reviewSelection(
  incoming: readonly SelectedUpload[],
  existing: readonly SelectedUpload[],
): SelectionReview {
  const accepted: SelectedUpload[] = [];
  const rejected: FileRejection[] = [];
  const seen = new Set(existing.map(selectionKey));
  for (const selected of incoming) {
    const rejection = rejectionFor(selected);
    const key = selectionKey(selected);
    if (rejection !== null) rejected.push(rejection);
    else if (seen.has(key)) rejected.push({ name: selected.file.name, path: selected.relativePath, reason: 'duplicate' });
    else {
      seen.add(key);
      accepted.push(selected);
    }
  }
  return { accepted, rejected };
}

function pluralizeFiles(count: number): string {
  return `${String(count)} file${count === 1 ? '' : 's'}`;
}

/**
 * Whether adding `incomingCount` files to `selectedCount` already-selected files would exceed the
 * selection limit.
 *
 * An over-limit attempt is refused whole rather than truncated: keeping the first N would silently
 * drop documents the user believes they are uploading.
 */
export function checkUploadSelectionLimit(counts: {
  readonly incomingCount: number;
  readonly selectedCount: number;
}): SelectionLimitExceeded | null {
  const { incomingCount, selectedCount } = counts;
  if (selectedCount + incomingCount <= MAX_UPLOAD_SELECTION_COUNT) {
    return null;
  }

  return {
    attemptedCount: incomingCount,
    selectedCount,
    limit: MAX_UPLOAD_SELECTION_COUNT,
  };
}

/** What went wrong and how to recover, phrased for the selection the user is looking at. */
export function describeSelectionLimitExceeded(limitExceeded: SelectionLimitExceeded): string {
  const { attemptedCount, limit, selectedCount } = limitExceeded;

  if (selectedCount === 0) {
    return `You selected ${pluralizeFiles(attemptedCount)}. Each upload takes up to ${limit.toLocaleString('en')} — choose ${limit.toLocaleString('en')} or fewer, then upload the rest in another group.`;
  }

  const remaining = Math.max(limit - selectedCount, 0);
  if (remaining === 0) {
    return `You already have ${pluralizeFiles(selectedCount)} selected, which is the limit for one upload. Upload these first, then start another group.`;
  }

  return `Adding ${pluralizeFiles(attemptedCount)} would put you over the ${limit.toLocaleString('en')}-file limit for one upload. Your selection is unchanged — add ${pluralizeFiles(remaining)} or fewer, then upload the rest in another group.`;
}

export function describeRejectionReason(rejection: FileRejection): string {
  if (rejection.reason === 'duplicate') {
    return 'already in the list';
  }

  if (rejection.reason === 'empty') {
    return 'empty file — nothing to upload';
  }

  if (rejection.reason === 'too_large') {
    return `exceeds the ${rejection.limitLabel ?? MAX_TEXT_UPLOAD_FILE_SIZE_LABEL} per-file limit`;
  }

  return `unsupported format - accepted: ${SUPPORTED_EXTENSIONS.join(', ')}`;
}

// ── Backend error mapping ────────────────────────────────────────────────
//
// The API returns a `code` on every ContractAnalyzerError (see
// apps/api/src/contract_analyzer_api/domain/errors.py). The api-client
// surfaces it as `ApiClientError.code`. Known upload-related codes map to
// short, actionable copy so every upload surface presents the same message
// regardless of the backend's raw `message` field.

/** Codes emitted by the backend for upload validation failures. */
type UploadErrorCode =
  | 'archive-entry-limit-exceeded'
  | 'batch-document-count-exceeded'
  | 'document-malware-detected'
  | 'document-ocr-processing-error'
  | 'duplicate-stored-document'
  | 'empty-batch-submission'
  | 'empty-document'
  | 'insufficient-permission'
  | 'invalid-archive'
  | 'invalid-document-content'
  | 'invalid-filename'
  | 'network-error'
  | 'tenant-document-quota-exceeded'
  | 'unsupported-document-type'
  | 'upload-too-large';

const UPLOAD_ERROR_MESSAGES: Readonly<Record<UploadErrorCode, string>> = {
  // The API counts every entry in the archive, including files it will skip as
  // unsupported, before reading any of them; the number the user sees must match.
  'archive-entry-limit-exceeded': `A ZIP archive holds more than ${String(MAX_ARCHIVE_ENTRY_COUNT)} files. Every entry counts, including unsupported files the upload would skip. Split the archive into smaller ZIPs of at most ${String(MAX_ARCHIVE_ENTRY_COUNT)} files and upload them one at a time.`,
  'batch-document-count-exceeded':
    'This submission references too many documents at once. Submit fewer documents per run.',
  'document-malware-detected':
    'One or more files were rejected by antivirus scanning and cannot be uploaded.',
  'document-ocr-processing-error':
    'Document processing failed. Try uploading the document again or contact support if it keeps failing.',
  'duplicate-stored-document':
    'A document with the same contents already exists in this project.',
  'empty-batch-submission': 'Select at least one file to upload.',
  'empty-document': 'One or more selected files are empty. Empty documents cannot be uploaded.',
  'insufficient-permission':
    'You do not have permission to upload documents in this project. Contact your workspace admin.',
  'invalid-archive': 'The uploaded ZIP archive is corrupted or is not a valid ZIP file.',
  'invalid-document-content':
    'One or more files contain content that is invalid for the selected file type.',
  'invalid-filename':
    'One or more filenames are invalid. Rename the file (remove unusual characters, keep an extension) and try again.',
  'network-error': 'Could not reach the server. Check your connection and try again.',
  'tenant-document-quota-exceeded':
    'Your workspace has reached its document upload quota. Contact your admin to raise the limit.',
  'unsupported-document-type': `Unsupported file type. Accepted formats: ${SUPPORTED_EXTENSIONS.join(', ')}.`,
  'upload-too-large': `One or more files exceed the size limit (${MAX_PDF_UPLOAD_FILE_SIZE_LABEL} per PDF, ${MAX_TEXT_UPLOAD_FILE_SIZE_LABEL} per other document, ${MAX_ARCHIVE_UPLOAD_FILE_SIZE_LABEL} per ZIP archive).`,
};

function extractErrorCode(error: unknown): string | null {
  if (error === null || typeof error !== 'object') return null;
  if (!('code' in error)) return null;
  const { code } = error;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

function isKnownUploadErrorCode(code: string): code is UploadErrorCode {
  return Object.prototype.hasOwnProperty.call(UPLOAD_ERROR_MESSAGES, code);
}

/** Network failures, timeouts, throttling and server faults can succeed when the same request is sent again. */
export function isRetryableRequestError(error: Error): boolean {
  if (error instanceof ApiClientTransportError) return true;
  return error instanceof ApiClientError && (error.status === 408 || error.status === 429 || error.status >= 500);
}

/**
 * Convert an error thrown by the API client (or any unknown error) into a
 * user-facing upload message. Known backend error codes get short, actionable
 * copy; otherwise the error's own message is used, with a generic fallback.
 */
export function getUploadErrorMessage(
  error: unknown,
  context?: { readonly filename?: string },
): string {
  const code = extractErrorCode(error);
  if (code !== null && isKnownUploadErrorCode(code)) {
    if (code === 'invalid-document-content' && error instanceof Error && error.message.trim().length > 0) {
      return context?.filename === undefined ? error.message : `${context.filename}: ${error.message}`;
    }
    const message = UPLOAD_ERROR_MESSAGES[code];
    return context?.filename === undefined ? message : `${context.filename}: ${message}`;
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return 'The upload could not be completed. Please try again.';
}
